import { useCallback, useEffect, useState } from 'react';
import {
  diagnoseApplicationFormNotify,
  fetchNotificationSettings,
  saveNotificationSettings,
  sendApplicationFormTestEmail,
  type ApplicationNotificationSettings,
} from '../../../lib/applicationFormSettings';
import {
  canSendApplicationFormEmailViaPortal,
  sendApplicationFormTestEmailViaPortal,
} from '../../../lib/applicationFormPortalEmail';
import { useAuth } from '../../../contexts/AuthContext';
import { useFormsSettingsRealtime } from '../../../hooks/useFormsSettingsRealtime';
import { AfSettingsSection } from './AfSettingsSection';

const PLACEHOLDERS = [
  '{application_no}',
  '{company_name}',
  '{customer_code}',
  '{delivery_schedule}',
  '{review_notes}',
  '{salesperson_code}',
  '{source}',
  '{submitted_by}',
];

function TemplateBlock({
  title,
  subjectKey,
  bodyKey,
  settings,
  patch,
  rows = 4,
}: {
  title: string;
  subjectKey: keyof ApplicationNotificationSettings;
  bodyKey: keyof ApplicationNotificationSettings;
  settings: ApplicationNotificationSettings;
  patch: <K extends keyof ApplicationNotificationSettings>(
    key: K,
    value: ApplicationNotificationSettings[K],
  ) => void;
  rows?: number;
}) {
  return (
    <details className="af-settings-template" open>
      <summary className="af-settings-template__summary">{title}</summary>
      <div className="af-settings-template__body">
        <label className="af-settings-field">
          <span>Subject</span>
          <input
            value={String(settings[subjectKey] ?? '')}
            onChange={(e) => patch(subjectKey, e.target.value)}
          />
        </label>
        <label className="af-settings-field af-settings-field--full">
          <span>Body</span>
          <textarea
            rows={rows}
            value={String(settings[bodyKey] ?? '')}
            onChange={(e) => patch(bodyKey, e.target.value)}
          />
        </label>
      </div>
    </details>
  );
}

export function ApplicationFormNotificationPanel() {
  const { authUser, profile } = useAuth();
  const [settings, setSettings] = useState<ApplicationNotificationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [diagnoseResult, setDiagnoseResult] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSettings(await fetchNotificationSettings());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load notification settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useFormsSettingsRealtime({
    tables: ['system_config'],
    onChange: () => {
      void load();
    },
  });

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!settings) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await saveNotificationSettings({
        ...settings,
        notifications_enabled: true,
      });
      setSettings(saved);
      setMessage('Email templates saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  function patch<K extends keyof ApplicationNotificationSettings>(
    key: K,
    value: ApplicationNotificationSettings[K],
  ) {
    setSettings((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  async function handleDiagnose() {
    setDiagnosing(true);
    setError(null);
    setMessage(null);
    setDiagnoseResult(null);
    try {
      const result = await diagnoseApplicationFormNotify();
      if (!result.ok) {
        setError(result.error ?? 'Diagnose failed. Is application-form-notify deployed?');
        return;
      }
      setDiagnoseResult(result.config ?? {});
      setMessage('Configuration check completed.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Diagnose failed.');
    } finally {
      setDiagnosing(false);
    }
  }

  async function handleTestEmail() {
    setTesting(true);
    setError(null);
    setMessage(null);
    const toEmail = authUser?.email ?? profile?.email ?? '';
    try {
      const result = canSendApplicationFormEmailViaPortal()
        ? await sendApplicationFormTestEmailViaPortal(toEmail)
        : await sendApplicationFormTestEmail(toEmail);
      if (!result.ok) {
        setError(result.error ?? 'Test email failed.');
        return;
      }
      const to = result.sent?.[0]?.to?.join(', ') ?? toEmail;
      setMessage(`Test email sent to ${to}. Check inbox and spam folder.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Test email failed.');
    } finally {
      setTesting(false);
    }
  }

  if (loading) {
    return <p className="af-settings-loading">Loading notification settings…</p>;
  }

  if (!settings) return null;

  return (
    <AfSettingsSection
      title="Email notifications"
      description="Recipients are resolved automatically: IT + Finance departments, salesperson code assignees, and the internal submitter. Edit templates below."
      badge="Templates"
      error={error}
      message={message}
      footer={
        <button
          type="submit"
          form="af-notification-settings-form"
          className="btn-primary btn-sm"
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save templates'}
        </button>
      }
    >
      <form
        id="af-notification-settings-form"
        className="af-settings-form"
        onSubmit={(e) => void handleSave(e)}
      >
        <div className="af-settings-troubleshoot">
          <h3 className="af-settings-subheading">Troubleshooting</h3>
          <p className="af-settings-field__hint">
            Test email is sent to your signed-in work address via the same path as DEM order emails.
          </p>
          <div className="af-settings-troubleshoot__actions">
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={diagnosing}
              onClick={() => void handleDiagnose()}
            >
              {diagnosing ? 'Checking…' : 'Check configuration'}
            </button>
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={testing}
              onClick={() => void handleTestEmail()}
            >
              {testing ? 'Sending…' : 'Send test email'}
            </button>
          </div>
          {diagnoseResult ? (
            <ul className="af-settings-diagnose-list">
              <li>
                Mail transport:{' '}
                <strong>{String(diagnoseResult.mail_transport ?? 'direct-graph')}</strong>
              </li>
              <li>
                Order-email proxy:{' '}
                <strong>{diagnoseResult.order_email_proxy_configured ? 'Configured' : 'Not set'}</strong>
              </li>
              <li>
                M365 sender email: <strong>{String(diagnoseResult.m365_sender_email ?? 'Not set')}</strong>
              </li>
              {diagnoseResult.graph_test_error ? (
                <li className="af-settings-diagnose-list__warn">
                  <strong>{String(diagnoseResult.graph_test_error)}</strong>
                </li>
              ) : null}
            </ul>
          ) : null}
        </div>

        <h3 className="af-settings-subheading">Submission templates</h3>
        <p className="af-settings-placeholder-hint">
          {PLACEHOLDERS.map((token) => (
            <code key={token} className="af-settings-placeholder-chip">
              {token}
            </code>
          ))}
        </p>
        <div className="af-settings-template-stack">
          <TemplateBlock
            title="Customer confirmation (public link)"
            subjectKey="submission_customer_subject"
            bodyKey="submission_customer_body"
            settings={settings}
            patch={patch}
          />
          <TemplateBlock
            title="IT / Finance alert"
            subjectKey="submission_finance_subject"
            bodyKey="submission_finance_body"
            settings={settings}
            patch={patch}
          />
          <TemplateBlock
            title="Salesperson alert"
            subjectKey="submission_sales_subject"
            bodyKey="submission_sales_body"
            settings={settings}
            patch={patch}
          />
          <TemplateBlock
            title="Internal submitter copy"
            subjectKey="submission_internal_subject"
            bodyKey="submission_internal_body"
            settings={settings}
            patch={patch}
          />
        </div>

        <h3 className="af-settings-subheading">After AR decision</h3>
        <div className="af-settings-decision-grid">
          <div className="af-settings-decision-card af-settings-decision-card--approved">
            <h4 className="af-settings-decision-card__title">Approved → customer</h4>
            <label className="af-settings-field">
              <span>Subject</span>
              <input
                value={settings.approved_customer_subject}
                onChange={(e) => patch('approved_customer_subject', e.target.value)}
              />
            </label>
            <label className="af-settings-field af-settings-field--full">
              <span>Body</span>
              <textarea
                rows={5}
                value={settings.approved_customer_body}
                onChange={(e) => patch('approved_customer_body', e.target.value)}
              />
            </label>
          </div>

          <div className="af-settings-decision-card af-settings-decision-card--rejected">
            <h4 className="af-settings-decision-card__title">Rejected → internal &amp; sales</h4>
            <label className="af-settings-field">
              <span>Subject</span>
              <input
                value={settings.rejected_internal_subject}
                onChange={(e) => patch('rejected_internal_subject', e.target.value)}
              />
            </label>
            <label className="af-settings-field af-settings-field--full">
              <span>Body</span>
              <textarea
                rows={5}
                value={settings.rejected_internal_body}
                onChange={(e) => patch('rejected_internal_body', e.target.value)}
              />
            </label>
          </div>
        </div>
      </form>
    </AfSettingsSection>
  );
}
